import React from "react";
import { useObjectDetails } from "./hooks/useObjectDetails";
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent, TileListContent } from '../../../components/Tile/TileContent';

// ObjectCard Component
const ObjectCard = ({ obj }) => (
  <div className="border-b border-gray-200 last:border-b-0 mb-4 pb-4 last:mb-0 last:pb-0">
    <h4 className="font-semibold text-gray-800 mb-2">
      Objekt ID: {obj.id}
    </h4>

    {/* Attributes Section */}
    {obj.attributes?.length > 0 && (
      <div className="mt-2">
        <strong className="text-gray-700 text-sm">Attribute:</strong>
        <ul className="list-none pl-2 mt-1 space-y-1">
          {obj.attributes.map((attr, index) => (
            <li key={`${attr.name}-${index}`} className="text-sm text-gray-600">
              <span className="font-medium text-gray-700">{attr.name}:</span> {attr.value}
              {attr.time && (
                <span className="text-gray-500 ml-1">
                  (Zeit: {new Date(attr.time).toLocaleString()})
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Relationships Section */}
    {obj.relationships?.length > 0 && (
      <div className="mt-2">
        <strong className="text-gray-700 text-sm">Beziehungen:</strong>
        <ul className="list-none pl-2 mt-1 space-y-1">
          {obj.relationships.map((rel, index) => (
            <li key={`${rel.objectId ?? "unknown"}-${index}`} className="text-sm text-gray-600">
              → <span className="font-medium text-gray-700">{rel.objectId ?? "Unbekannt"}</span>
              <span className="text-gray-500 ml-1">
                ({rel.qualifier || "Kein Qualifier"})
              </span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* No Details Message */}
    {!obj.attributes?.length && !obj.relationships?.length && (
      <p className="italic text-gray-500 mt-2 text-sm">
        Keine Attribute oder Beziehungen für dieses Objekt vorhanden.
      </p>
    )}
  </div>
);

const ObjectDetails = ({ activeFile, objectTypeToShow = "Invoice" }) => {
  // Pass activeFile and objectTypeToShow to the hook for filtering
  const { ocelData, loading, error } = useObjectDetails(activeFile, objectTypeToShow);

  // Determine states - objects are already filtered by the backend
  const hasValidData = ocelData && ocelData.objects;
  const filteredObjects = hasValidData ? ocelData.objects : [];

  const showNoOcelData = !loading && !error && !hasValidData;
  const showNoFilteredObjects = !loading && !error && hasValidData && filteredObjects.length === 0;
  const showObjectCards = !loading && !error && hasValidData && filteredObjects.length > 0;

  return (
    <BaseTile
      title={`Details für Objekte vom Typ: ${objectTypeToShow}`}
      loading={loading}
      error={error}
      minHeight="lg"
      className="h-full"
    >
      <TileContent layout="flex" direction="column" grow>
        {/* No OCEL Data State */}
        {showNoOcelData && (
          <TileStatusContent
            status="empty"
            title="Keine OCEL-Daten verfügbar"
            message="Keine OCEL-Daten verfügbar oder ungültiges Format."
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
        )}

        {/* No Filtered Objects State */}
        {showNoFilteredObjects && (
          <TileStatusContent
            status="empty"
            title="Keine Objekte gefunden"
            message={`Keine Objekte vom Typ "${objectTypeToShow}" im Log gefunden.`}
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            }
          />
        )}

        {/* Object Cards List */}
        {showObjectCards && (
          <TileListContent maxHeight={500} className="space-y-0">
            {filteredObjects.map((obj) => (
              <ObjectCard key={obj.id} obj={obj} />
            ))}
          </TileListContent>
        )}

        {/* Show total count if available */}
        {showObjectCards && ocelData.total_count > filteredObjects.length && (
          <div className="text-sm text-gray-500 mt-2 text-center">
            Zeige {filteredObjects.length} von {ocelData.total_count} Objekten
          </div>
        )}
      </TileContent>
    </BaseTile>
  );
};

export default ObjectDetails;
